// apps/web/src/pages/FelSettingsPage.tsx
//
// Super-admin only — toggle INFILE FEL certificador between DEMO and Production.
// The setting lives in public.system_settings.fel_environment and is read by
// the fel-emit-dte edge function on every invocation.
import { useEffect, useState } from 'react';
import { Loader2, ShieldCheck, FlaskConical, AlertTriangle, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { usePermissions } from '@/hooks/usePermissions';
import {
  getFelEnvironment, setFelEnvironment, type FelEnvironment,
} from '@/services/payments';

export function FelSettingsPage() {
  const { hasRole } = usePermissions();
  const isSuperAdmin = hasRole('super_admin');

  const [current, setCurrent] = useState<FelEnvironment | null>(null);
  const [pending, setPending] = useState<FelEnvironment | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const env = await getFelEnvironment();
        if (!cancelled) setCurrent(env);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'No se pudo cargar el entorno');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!isSuperAdmin) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="p-6 text-muted-foreground">
            Esta sección está disponible únicamente para super administradores.
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSelect = (env: FelEnvironment) => {
    if (env === current) return;
    setPending(env);
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (!pending) return;
    setSaving(true);
    setError(null);
    try {
      await setFelEnvironment(pending);
      setCurrent(pending);
      setConfirmOpen(false);
      setPending(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar el cambio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <FileText className="w-6 h-6" />
          Facturación electrónica (FEL)
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Selecciona el entorno del certificador INFILE. Todas las emisiones de DTE futuras
          usarán esta configuración en tiempo real (no requiere redeploy).
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md bg-rose-500/10 text-rose-700 border border-rose-500/30 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Entorno INFILE FEL</span>
              {current === 'production' ? (
                <Badge className="bg-emerald-500/15 text-emerald-600 border border-emerald-500/30">
                  <ShieldCheck className="w-3 h-3 mr-1 inline" /> Producción activa
                </Badge>
              ) : (
                <Badge className="bg-amber-500/15 text-amber-600 border border-amber-500/30">
                  <FlaskConical className="w-3 h-3 mr-1 inline" /> DEMO activo
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleSelect('demo')}
                className={`text-left p-4 rounded-lg border-2 transition-all ${
                  current === 'demo'
                    ? 'border-amber-500 bg-amber-500/5'
                    : 'border-muted hover:border-amber-500/50'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <FlaskConical className="w-5 h-5 text-amber-600" />
                  <span className="font-medium">DEMO</span>
                  {current === 'demo' && <Badge variant="outline" className="ml-auto">Actual</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">
                  Ambiente de pruebas de INFILE. Los DTEs se certifican con serie
                  <code className="mx-1">**PRUEBAS**</code> y no tienen validez fiscal.
                </p>
              </button>

              <button
                type="button"
                onClick={() => handleSelect('production')}
                className={`text-left p-4 rounded-lg border-2 transition-all ${
                  current === 'production'
                    ? 'border-emerald-500 bg-emerald-500/5'
                    : 'border-muted hover:border-emerald-500/50'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck className="w-5 h-5 text-emerald-600" />
                  <span className="font-medium">Producción</span>
                  {current === 'production' && <Badge variant="outline" className="ml-auto">Actual</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">
                  Ambiente real. Cada emisión genera una factura electrónica oficial ante la SAT
                  y consume saldo del contrato con INFILE.
                </p>
              </button>
            </div>

            <div className="text-xs text-muted-foreground border-t pt-3">
              <p className="font-medium mb-1">Importante:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>El cambio tiene efecto inmediato — no requiere redeploy de edge functions.</li>
                <li>Las credenciales de cada entorno se gestionan como Supabase secrets
                  (<code>FEL_DEMO_*</code> / <code>FEL_PROD_*</code>).</li>
                <li>El emisor NIT también cambia con el entorno: DEMO usa <code>11700830K</code>,
                  Producción usa <code>36856703</code>.</li>
                <li>Cambiar a DEMO detiene la emisión real hasta que se reactive Producción.</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {!loading && current === 'demo' && (
        <Card className="border-amber-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <FlaskConical className="w-5 h-5" />
              Modo DEMO activo
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-2">
            <p>
              Las facturas que emitas ahora se certifican contra el ambiente de pruebas de INFILE.
              Los DTEs reciben un UUID válido pero la serie es <code>**PRUEBAS**</code> y SAT no los considera oficiales.
            </p>
            <p>
              Útil para validar la integración antes de cobrar a clientes reales.
            </p>
          </CardContent>
        </Card>
      )}

      <Dialog open={confirmOpen} onOpenChange={(o) => !o && !saving && setConfirmOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Cambiar a {pending === 'production' ? 'Producción' : 'DEMO'}
            </DialogTitle>
            <DialogDescription>
              {pending === 'production'
                ? 'Las próximas facturas se certificarán ante SAT y consumirán saldo del contrato con INFILE. ¿Confirmas?'
                : 'Las próximas facturas se emitirán contra el ambiente DEMO (sin validez fiscal). ¿Confirmas?'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleConfirm} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
